// Printing from a list row (boards PrintMenuDesk, PrintMenuPhone, PrintMenuEverywhere;
// docs/plans/print-menu.md): the row's printer opens a small menu, the paper last used ticked
// and first, so P then Enter reprints. On a touch screen the same choices come up as a sheet,
// Share PDF first. "Print or save…" opens the full dialog with its preview, as on the page.
import { Kbd, Menu, MenuItem, MenuLabel, MenuSeparator, Modal, cx } from '@ims/ui'
import { Download, Eye, Printer, Share2 } from '@ims/ui/icons'
import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'
import { money } from '../lib/format.ts'
import { finePointer } from '../lib/pointer.ts'
import { paperOrder, readPrintChoice, savePrintChoice } from '../lib/print-choice.ts'
import type { PrintFormat } from '../print.tsx'
import { useSettings } from '../hooks.ts'
import {
  DocPrintDialog,
  PrintDialog,
  sharesFiles,
  useBillOutput,
  useDocOutput,
  type DocToPrint,
} from './print-dialog.tsx'

const MENU_WIDTH = 300
const ICON = <Printer className="size-4" aria-hidden />
/** The printer button's look (`IconButton`'s), lit while its menu is open. */
const ICON_LOOK =
  'inline-flex size-8 items-center justify-center tap-target rounded-xl text-muted transition-colors hover:bg-soft hover:text-ink aria-expanded:border-primary aria-expanded:bg-primary-light aria-expanded:text-primary'

/** The trigger's classes: the printer's look plus `extra`, or `extra` alone for a custom trigger. */
const lookOf = (custom: boolean, extra?: string) => (custom ? extra : cx(ICON_LOOK, extra))

const PAPER: Record<PrintFormat, { title: string; about: string; key: string }> = {
  receipt: { title: 'Print receipt', about: '80 mm', key: 'R' },
  a4: { title: 'Print A4', about: 'Full page', key: 'A' },
}

/** Opens the print menu of the row `id` (a list's P key), as a tap on its printer would. */
export function openPrintMenu(id: string): void {
  document.querySelector<HTMLElement>(`[data-print-for="${CSS.escape(id)}"] button`)?.click()
}

/**
 * The menu's letters (R, A, D, O): each picks its row, in the order the rows are drawn. The
 * list under the menu has letters of its own, so the key goes no further.
 */
function useLetterKeys(keys: () => string[]) {
  const wrap = useRef<HTMLDivElement>(null)
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.ctrlKey || e.metaKey || e.altKey) return
    if (!(e.target as HTMLElement).closest('[role="menu"]')) return
    const at = keys().indexOf(e.key.toUpperCase())
    if (at < 0) return
    e.preventDefault()
    e.stopPropagation()
    const rows = wrap.current?.querySelectorAll<HTMLElement>('[role="menu"] [role^="menuitem"]')
    rows?.[at]?.click()
  }
  return { wrap, onKeyDown }
}

interface TriggerProps {
  /** The button's accessible name, e.g. "Print INV-…" or "Reprint the bill of …". */
  label: string
  /** What the button shows: the printer by default (then `triggerClassName` adds to its look). */
  trigger?: ReactNode
  triggerClassName?: string
  /** `auto` for a row of a long list (opens above near the bottom of the screen). */
  side?: 'below' | 'above' | 'auto'
  /** On the wrapper, e.g. `flex-1` for a trigger that fills its cell. */
  className?: string
}

/**
 * A bill's print button for a list row: Print on the paper last used (ticked, Enter),
 * the other paper, Download PDF, or Print or save… for the preview. Every one is a copy,
 * counted and labelled like the dialog's.
 */
export function BillPrintMenu({
  invoiceId,
  serverName,
  label,
  trigger,
  triggerClassName,
  side = 'auto',
  className,
}: TriggerProps & {
  invoiceId: string
  serverName: (id: string | null) => string | null
}) {
  const [dialog, setDialog] = useState(false)
  const [sheet, setSheet] = useState(false)
  const { wrap, onKeyDown } = useLetterKeys(() => [
    ...paperOrder(readPrintChoice()).map((f) => PAPER[f].key),
    'D',
    'O',
  ])
  return (
    <div
      ref={wrap}
      data-print-for={invoiceId}
      onKeyDown={onKeyDown}
      className={cx('flex', className)}
    >
      {finePointer ? (
        <Menu
          label={label}
          title={label}
          trigger={trigger ?? ICON}
          triggerClassName={lookOf(!!trigger, triggerClassName)}
          side={side}
          width={MENU_WIDTH}
          fixed
          className="flex min-w-0 flex-1"
        >
          <BillRows invoiceId={invoiceId} serverName={serverName} onMore={() => setDialog(true)} />
        </Menu>
      ) : (
        <button
          type="button"
          aria-label={label}
          aria-haspopup="dialog"
          className={lookOf(!!trigger, triggerClassName)}
          onClick={() => setSheet(true)}
        >
          {trigger ?? ICON}
        </button>
      )}
      {sheet && (
        <BillSheet
          invoiceId={invoiceId}
          serverName={serverName}
          onMore={() => {
            setSheet(false)
            setDialog(true)
          }}
          onClose={() => setSheet(false)}
        />
      )}
      {dialog && (
        <PrintDialog
          invoiceId={invoiceId}
          serverName={serverName}
          onClose={() => setDialog(false)}
        />
      )}
    </div>
  )
}

/** What the next copy will say, under the rows. */
function copyLine(copy: { label: string | null } | null | undefined, draft: boolean): string {
  if (!copy || draft) return ''
  return copy.label ? `Prints as ${copy.label}` : 'First print of this bill'
}

function BillRows({
  invoiceId,
  serverName,
  onMore,
}: {
  invoiceId: string
  serverName: (id: string | null) => string | null
  onMore: () => void
}) {
  const settings = useSettings()
  const out = useBillOutput(invoiceId, serverName, null)
  const chosen = readPrintChoice()
  const printOn = (format: PrintFormat) => {
    savePrintChoice(format)
    void out.print(format)
  }
  const line = copyLine(out.copy, out.invoice?.status === 'draft')
  return (
    <>
      <MenuLabel>
        {out.invoice
          ? [out.invoice.number ?? 'Number on sync', money(out.invoice.total_paisa, settings)].join(
              ' · ',
            )
          : 'Print'}
      </MenuLabel>
      {paperOrder(chosen).map((f) => (
        <MenuItem
          key={f}
          checked={f === chosen}
          icon={ICON}
          hint={<Kbd>{PAPER[f].key}</Kbd>}
          onSelect={() => printOn(f)}
        >
          <span className="block font-semibold">{PAPER[f].title}</span>
          <span className="block text-xs text-muted">
            {f === chosen ? `${PAPER[f].about} · last used here` : PAPER[f].about}
          </span>
        </MenuItem>
      ))}
      <MenuItem
        icon={<Download className="size-4" aria-hidden />}
        hint={<Kbd>D</Kbd>}
        onSelect={() => void out.save('pdf', chosen)}
      >
        <span className="font-semibold">Download PDF</span>
      </MenuItem>
      <MenuSeparator />
      <MenuItem icon={<Eye className="size-4" aria-hidden />} hint={<Kbd>O</Kbd>} onSelect={onMore}>
        <span className="block font-semibold">Print or save…</span>
        <span className="block text-xs text-muted">See it first, Share PDF</span>
      </MenuItem>
      {line && (
        <p className="mx-1 mt-1 border-t border-border px-2 pt-2 pb-1 text-xs text-muted">{line}</p>
      )}
    </>
  )
}

/** A full-width row of the phone's sheet. */
function SheetRow({
  icon,
  title,
  about,
  chosen = false,
  busy = false,
  onClick,
}: {
  icon: ReactNode
  title: string
  about?: string
  chosen?: boolean
  busy?: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      aria-current={chosen || undefined}
      className={cx(
        'flex min-h-12 w-full items-center gap-3 rounded-xl px-3 py-2 text-left transition-colors disabled:opacity-60',
        chosen ? 'bg-primary-light text-primary' : 'text-ink hover:bg-soft',
      )}
    >
      <span
        className={cx(
          'grid size-9 shrink-0 place-items-center rounded-xl',
          chosen ? 'bg-surface' : 'bg-soft',
        )}
      >
        {icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block font-semibold">{title}</span>
        {about && <span className="block text-xs text-muted">{about}</span>}
      </span>
    </button>
  )
}

function BillSheet({
  invoiceId,
  serverName,
  onMore,
  onClose,
}: {
  invoiceId: string
  serverName: (id: string | null) => string | null
  onMore: () => void
  onClose: () => void
}) {
  const settings = useSettings()
  const chosen = readPrintChoice()
  const out = useBillOutput(invoiceId, serverName, chosen)
  const [busy, setBusy] = useState<string | null>(null)
  const run = async (what: string, work: () => Promise<unknown>) => {
    setBusy(what)
    try {
      const result = await work()
      if (result !== 'closed' && result !== 'failed') onClose()
    } finally {
      setBusy(null)
    }
  }
  const inv = out.invoice
  const line = copyLine(out.copy, inv?.status === 'draft')
  return (
    <Modal open onClose={onClose} title={inv?.number ?? 'Print or save'} size="sm">
      <div className="-mt-1 space-y-1">
        {inv && (
          <p className="mb-2 text-sm text-muted">
            {[inv.customer_name, money(inv.total_paisa, settings)].filter(Boolean).join(' · ')}
          </p>
        )}
        {sharesFiles && (
          <SheetRow
            icon={<Share2 className="size-4" aria-hidden />}
            title="Share PDF"
            about="WhatsApp, Viber, email…"
            chosen
            busy={busy === 'share'}
            onClick={() => void run('share', () => out.save('share', chosen))}
          />
        )}
        {paperOrder(chosen).map((f) => (
          <SheetRow
            key={f}
            icon={ICON}
            title={PAPER[f].title}
            about={PAPER[f].about}
            chosen={!sharesFiles && f === chosen}
            busy={busy === f}
            onClick={() =>
              void run(f, async () => {
                savePrintChoice(f)
                await out.print(f)
              })
            }
          />
        ))}
        <SheetRow
          icon={<Download className="size-4" aria-hidden />}
          title="Download PDF"
          busy={busy === 'pdf'}
          onClick={() => void run('pdf', () => out.save('pdf', chosen))}
        />
        <div role="separator" className="mx-1 my-1 h-px bg-border" />
        <SheetRow
          icon={<Eye className="size-4" aria-hidden />}
          title="Print or save…"
          about="See it first"
          onClick={onMore}
        />
        {line && <p className="mt-2 border-t border-border px-3 pt-3 text-sm text-muted">{line}</p>}
      </div>
    </Modal>
  )
}

/**
 * A quotation's, an order's or a purchase order's print button for a list row: A4 only, so
 * Print A4 (ticked, Enter), Download PDF, or Print or save… for the preview. `load` builds the
 * document when the menu opens.
 */
export function DocPrintMenu({
  id,
  load,
  label,
  trigger,
  triggerClassName,
  side = 'auto',
  className,
}: TriggerProps & { id: string; load: () => Promise<DocToPrint> }) {
  const [dialog, setDialog] = useState<DocToPrint | null>(null)
  const [sheet, setSheet] = useState(false)
  const { wrap, onKeyDown } = useLetterKeys(() => ['A', 'D', 'O'])
  return (
    <div ref={wrap} data-print-for={id} onKeyDown={onKeyDown} className={cx('flex', className)}>
      {finePointer ? (
        <Menu
          label={label}
          title={label}
          trigger={trigger ?? ICON}
          triggerClassName={lookOf(!!trigger, triggerClassName)}
          side={side}
          width={MENU_WIDTH}
          fixed
          className="flex min-w-0 flex-1"
        >
          <DocRows load={load} onMore={setDialog} />
        </Menu>
      ) : (
        <button
          type="button"
          aria-label={label}
          aria-haspopup="dialog"
          className={lookOf(!!trigger, triggerClassName)}
          onClick={() => setSheet(true)}
        >
          {trigger ?? ICON}
        </button>
      )}
      {sheet && (
        <DocSheet
          load={load}
          onMore={(item) => {
            setSheet(false)
            setDialog(item)
          }}
          onClose={() => setSheet(false)}
        />
      )}
      {dialog && <DocPrintDialog {...dialog} onClose={() => setDialog(null)} />}
    </div>
  )
}

/** The document, loaded once when the menu or sheet opens: the promise, and what it gave. */
function useLoaded(load: () => Promise<DocToPrint>) {
  const [pending] = useState(load)
  const [item, setItem] = useState<DocToPrint | null>(null)
  useEffect(() => {
    let live = true
    void pending.then(
      (it) => live && setItem(it),
      () => {},
    )
    return () => {
      live = false
    }
  }, [pending])
  return { pending, item }
}

function DocRows({
  load,
  onMore,
}: {
  load: () => Promise<DocToPrint>
  onMore: (item: DocToPrint) => void
}) {
  const { pending, item } = useLoaded(load)
  const out = useDocOutput(null)
  return (
    <>
      <MenuLabel>{item ? item.summary : 'Print'}</MenuLabel>
      <MenuItem
        checked
        icon={ICON}
        hint={<Kbd>A</Kbd>}
        onSelect={() => void pending.then((it) => out.print(it))}
      >
        <span className="block font-semibold">Print A4</span>
        <span className="block text-xs text-muted">The page, with your logo</span>
      </MenuItem>
      <MenuItem
        icon={<Download className="size-4" aria-hidden />}
        hint={<Kbd>D</Kbd>}
        onSelect={() => void pending.then((it) => out.save('pdf', it))}
      >
        <span className="font-semibold">Download PDF</span>
      </MenuItem>
      <MenuSeparator />
      <MenuItem
        icon={<Eye className="size-4" aria-hidden />}
        hint={<Kbd>O</Kbd>}
        onSelect={() => void pending.then(onMore)}
      >
        <span className="block font-semibold">Print or save…</span>
        <span className="block text-xs text-muted">See it first, Share PDF</span>
      </MenuItem>
    </>
  )
}

function DocSheet({
  load,
  onMore,
  onClose,
}: {
  load: () => Promise<DocToPrint>
  onMore: (item: DocToPrint) => void
  onClose: () => void
}) {
  const { pending, item } = useLoaded(load)
  const out = useDocOutput(item)
  const [busy, setBusy] = useState<string | null>(null)
  const run = async (what: string, work: () => Promise<unknown>) => {
    setBusy(what)
    try {
      const result = await work()
      if (result !== 'closed' && result !== 'failed') onClose()
    } finally {
      setBusy(null)
    }
  }
  return (
    <Modal open onClose={onClose} title={item?.doc.number ?? 'Print'} size="sm">
      <div className="-mt-1 space-y-1">
        {item && <p className="mb-2 text-sm text-muted">{item.summary}</p>}
        {sharesFiles && (
          <SheetRow
            icon={<Share2 className="size-4" aria-hidden />}
            title="Share PDF"
            about="WhatsApp, Viber, email…"
            chosen
            busy={busy === 'share'}
            onClick={() => void run('share', async () => out.save('share', await pending))}
          />
        )}
        <SheetRow
          icon={ICON}
          title="Print A4"
          chosen={!sharesFiles}
          onClick={() => void run('print', async () => out.print(await pending))}
        />
        <SheetRow
          icon={<Download className="size-4" aria-hidden />}
          title="Download PDF"
          busy={busy === 'pdf'}
          onClick={() => void run('pdf', async () => out.save('pdf', await pending))}
        />
        <div role="separator" className="mx-1 my-1 h-px bg-border" />
        <SheetRow
          icon={<Eye className="size-4" aria-hidden />}
          title="Print or save…"
          about="See it first"
          onClick={() => void pending.then(onMore)}
        />
      </div>
    </Modal>
  )
}
